// DTOs de C8 — análisis de paros (coordinador).
//
// Endpoints:
//   GET /api/coordinador/analisis-paros?desde&hasta&cliente_id&planta_id&area_id&linea_id
//
// Responde con:
//   - Distribución por causal madre (categoría operativa)
//   - Distribución por causal específica (anidada)
//   - Tendencia temporal por día × causal madre
//   - Top causales "Otro" (alerta de calidad de dato)
//
// MIGRACIÓN: campos `categoria_madre` (string/enum hardcodeado) reemplazados por
// `categoria_id`, `categoria_nombre`, `categoria_color` — valores dinámicos del
// catálogo `categorias_paro`. Categorías nuevas aparecen sin cambio de código.
import { z } from 'zod';
// =============================================================================
// Filtros
// =============================================================================
export const analisisParosQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    cliente_id: z.string().uuid().optional(),
    planta_id: z.string().uuid().optional(),
    area_id: z.string().uuid().optional(),
    linea_id: z.string().uuid().optional(),
});
// =============================================================================
// Recursos
// =============================================================================
const clienteResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
});
const plantaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
});
const areaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
});
const lineaResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    area_id: z.string().uuid(),
});
// =============================================================================
// Distribución por causal madre
// =============================================================================
export const causalMadreDistribucionSchema = z.object({
    causal_madre_id: z.string().uuid(),
    causal_madre_nombre: z.string(),
    /** ID de la categoría en el catálogo `categorias_paro`. */
    categoria_id: z.string().uuid(),
    /** Nombre de la categoría (dinámico — no hardcodeado). */
    categoria_nombre: z.string(),
    /** Color hex de la categoría para visuales. Null si no configurado. */
    categoria_color: z.string().nullable(),
    paros_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
    pct_minutos: z.number().min(0).max(100),
    /**
     * Pérdida en pesos = valor no producido por los paros de la causal
     * (Σ minutos × capacidad/60 × precio del SKU del registro padre — misma
     * lógica de /analisis-lineas). Null si: el usuario no puede ver costos,
     * ningún registro afectado tiene precio vigente, o la categoría de la
     * causal tiene afecta_disponibilidad=false (paros programados no pierden
     * disponibilidad por definición de negocio). ADITIVO 2026-09 (Informe
     * Gerencial) — nullable para no romper consumidores previos.
     */
    perdida_pesos: z.number().min(0).nullable(),
});
// =============================================================================
// Distribución por causal específica (con referencia a madre)
// =============================================================================
export const causalEspecificaDistribucionSchema = z.object({
    causal_id: z.string().uuid(),
    causal_nombre: z.string(),
    causal_madre_id: z.string().uuid(),
    causal_madre_nombre: z.string(),
    /** ID de la categoría en el catálogo `categorias_paro`. */
    categoria_id: z.string().uuid(),
    /** Nombre de la categoría (dinámico — no hardcodeado). */
    categoria_nombre: z.string(),
    /** Color hex de la categoría para visuales. Null si no configurado. */
    categoria_color: z.string().nullable(),
    paros_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
    pct_minutos: z.number().min(0).max(100),
    /** True si el nombre contiene "otro" (case insensitive) — flag de calidad. */
    es_otro: z.boolean(),
    /** Pérdida en pesos de la causal — misma semántica que en la madre (ver arriba). */
    perdida_pesos: z.number().min(0).nullable(),
});
// =============================================================================
// Tendencia temporal — un row por (fecha, causal_madre)
// =============================================================================
export const tendenciaParoSchema = z.object({
    fecha: z.string().date(),
    causal_madre_id: z.string().uuid(),
    causal_madre_nombre: z.string(),
    minutos: z.number().min(0),
});
// =============================================================================
// Alerta de calidad — top causales "Otro"
// =============================================================================
export const alertaOtroSchema = z.object({
    /** Suma de minutos de todas las causales marcadas como "Otro". */
    minutos_otros: z.number().min(0),
    /** Porcentaje del total. */
    pct_otros: z.number().min(0).max(100),
    /** Lista de causales específicas marcadas como Otro, ordenadas por minutos. */
    causales: z.array(causalEspecificaDistribucionSchema),
});
// =============================================================================
// ¿Cuándo ocurren? — desglose por turno y perfil por hora del día
// =============================================================================
export const paroPorTurnoSchema = z.object({
    turno_id: z.string().uuid(),
    turno_numero: z.number().int(),
    turno_nombre: z.string(),
    paros_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
});
export const paroPorHoraSchema = z.object({
    /** Hora del día 0-23 (de hora_inicio_bloque del registro). */
    hora: z.number().int().min(0).max(23),
    paros_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
});
// =============================================================================
// Producción/cumplimiento por turno y por hora — mismos filtros que por_turno/
// por_hora de paros (reutiliza el mismo WHERE en el endpoint, sin el join de
// paro). cumplimiento_meta_pct = real / capacidad estándar (skus_lineas), NULL
// si no hay meta configurada. No hay cumplimiento vs. programa acá: el
// programa es diario, no por turno/hora (mismo criterio que /cumplimiento).
// =============================================================================
export const produccionPorTurnoSchema = z.object({
    turno_id: z.string().uuid(),
    turno_numero: z.number().int(),
    turno_nombre: z.string(),
    cantidad_real: z.number().min(0),
    meta_acumulada: z.number().min(0),
    cumplimiento_meta_pct: z.number().min(0).nullable(),
});
export const produccionPorHoraSchema = z.object({
    hora: z.number().int().min(0).max(23),
    cantidad_real: z.number().min(0),
    meta_acumulada: z.number().min(0),
    cumplimiento_meta_pct: z.number().min(0).nullable(),
});
// =============================================================================
// Response GET /api/coordinador/analisis-paros
// =============================================================================
export const coordinadorAnalisisParosSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(plantaResumenSchema),
    }),
    filtros: z.object({
        desde: z.string().date(),
        hasta: z.string().date(),
        cliente_id: z.string().uuid().nullable(),
        planta_id: z.string().uuid().nullable(),
        area_id: z.string().uuid().nullable(),
        linea_id: z.string().uuid().nullable(),
    }),
    recursos: z.object({
        clientes: z.array(clienteResumenSchema),
        plantas: z.array(plantaResumenSchema),
        areas: z.array(areaResumenSchema),
        lineas: z.array(lineaResumenSchema),
    }),
    totales: z.object({
        /** B2 "paro total registrado": TODOS los paros cerrados, incluidos programados. */
        paros_count: z.number().int().min(0),
        minutos_totales: z.number().min(0),
        horas_totales: z.number().min(0),
        dias_con_paros: z.number().int().min(0),
        /** DN1: paros abiertos (sin hora_fin) del mismo scope — aparte, no suman. */
        en_curso: z.number().int().min(0).optional(),
    }),
    /** Causales madre ordenadas por minutos DESC. */
    por_causal_madre: z.array(causalMadreDistribucionSchema),
    /** Causales específicas ordenadas por minutos DESC (incluye `es_otro` flag). */
    por_causal_especifica: z.array(causalEspecificaDistribucionSchema),
    /** Tendencia: row por (fecha, causal_madre). Ordenado por fecha ASC. */
    tendencia_temporal: z.array(tendenciaParoSchema),
    /** Paros agregados por turno (T1/T2/T3) en el rango. Ordenado por minutos DESC. */
    por_turno: z.array(paroPorTurnoSchema),
    /** Perfil horario: paros agregados por hora del día (0-23). Ordenado por hora ASC. */
    por_hora: z.array(paroPorHoraSchema),
    /** Producción/cumplimiento agregado por turno, mismos filtros que por_turno. */
    produccion_por_turno: z.array(produccionPorTurnoSchema),
    /** Producción/cumplimiento agregado por hora, mismos filtros que por_hora. */
    produccion_por_hora: z.array(produccionPorHoraSchema),
    /** Alerta de calidad de dato — top "Otro". */
    alerta_otro: alertaOtroSchema,
});
//# sourceMappingURL=coordinador_analisis_paros.js.map